import { forwardRef, HTMLAttributes } from "react";
import { classNames } from "@/utils/classNames";
import { fontWeightMapping, textSizeMapping } from "./tailwindMappings";
import { BoxProps } from "./boxHelpers";

export type AvatarProps = {
  color?: "neutral" | "secondary" | "primary";
  className?: string;
  textSize?: BoxProps["textSize"];
  fontWeight?: BoxProps["fontWeight"];
  size?: "sm" | "md" | "lg";
};

function prepareProps<T extends AvatarProps>({
  className,
  color,
  textSize = "lg",
  fontWeight = "bold",
  size = "sm",
  ...props
}: T) {
  return {
    className: classNames(
      className,
      "flex-none",
      "overflow-hidden",
      "rounded-full",
      size === "sm" && ["w-12", "h-12"],
      size === "md" && ["w-24", "h-24"],
      size === "lg" && ["w-40", "h-40"],
      "flex",
      "items-center",
      "justify-center",
      textSizeMapping[textSize],
      color === "secondary"
        ? "bg-secondary-light color-default"
        : color === "primary"
          ? "bg-primary-light color-primary-dark"
          : "bg-gray-hard-dimmed75 color-gray-xx-soft",
      fontWeightMapping[fontWeight],
    ),
    ...props,
  };
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps & HTMLAttributes<HTMLDivElement>>(
  function Avatar({ children, ...props }, ref) {
    return (
      <div ref={ref} {...prepareProps(props)}>
        {children}
      </div>
    );
  },
);

export const AvatarButton = forwardRef<
  HTMLButtonElement,
  AvatarProps & HTMLAttributes<HTMLButtonElement>
>(function AvatarButton({ children, ...props }, ref) {
  return (
    <button ref={ref} {...prepareProps(props)}>
      {children}
    </button>
  );
});
